import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import type * as React from "react"

import { cn } from "@/lib"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl font-semibold transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary:
          "bg-button-primary text-secondary border-button-primary disabled:bg-button-disabled disabled:border-button-disabled",
        secondary:
          "bg-button-base text-white disabled:bg-button-disabled disabled:opacity-50 disabled:cursor-not-allowed",
        outline:
          "bg-transparent !text-[#F2F0EB] border border-[#FFFFFF] hover:bg-[#FFFFFF] hover:!text-[#020202]  disabled:opacity-20",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        ghost: "hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        "outline-gradient":
          "bg-transparent !text-[#F2F0EB] border hover:!bg-transparent hover:!text-[#F2F0EB] disabled:opacity-20",
        "primary-white":
          "rounded-full bg-[#F2F0EB] text-[#020202] hover:bg-[#E8E6E1] disabled:opacity-20",
        "primary-terracotta":
          "rounded-full bg-brand-terracotta text-[#F2F0EB] hover:bg-brand-terracotta-hover disabled:opacity-20",
      },
      size: {
        default: "h-9 px-4 py-2 text-sm has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-10 text-sm px-5 has-[>svg]:px-3",
        md: "h-12 text-[14px] px-6",
        lg: "h-14 text-lg px-8 has-[>svg]:px-6",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  },
)

function Button({
  className,
  variant = "primary",
  size = "md",
  fullWidth = false,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    isLoading?: boolean
  }) {
  const Comp = asChild ? Slot : "button"
  const isDisabled = disabled || isLoading

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      disabled={isDisabled}
      className={cn(
        buttonVariants({ variant, size, fullWidth, className }),
        isDisabled && "cursor-not-allowed",
      )}
      {...props}
    >
      {isLoading ? <Loader2 className="size-4 animate-spin" /> : children}
    </Comp>
  )
}

export { Button, buttonVariants }
